import { useState, useEffect, useCallback } from 'react';
import { trackEnpalForYouEngagement } from '../services/enpalForYouService';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY = 'a2hs-dismissed';
const DISMISS_DURATION_DAYS = 7;

function isDismissed(): boolean {
  const dismissed = localStorage.getItem(STORAGE_KEY);
  if (!dismissed) return false;
  
  const dismissedAt = parseInt(dismissed, 10);
  const now = Date.now();
  const daysSinceDismissed = (now - dismissedAt) / (1000 * 60 * 60 * 24);
  
  return daysSinceDismissed < DISMISS_DURATION_DAYS;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const nav = window.navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) {
    return true;
  }
  const displayModes = ['standalone', 'fullscreen', 'minimal-ui'] as const;
  return displayModes.some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches);
}

function detectPlatform(): 'ios' | 'android' | 'other' {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  if (/Android/.test(ua)) {
    return 'android';
  }
  return 'other';
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(isStandalone);
  const [dismissed, setDismissed] = useState(isDismissed);
  const [platform] = useState(detectPlatform);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const installedHandler = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      trackEnpalForYouEngagement({ type: 'pwa_app_installed' });
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | null> => {
    if (!deferredPrompt) return null;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
    return outcome;
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setDismissed(true);
  }, []);

  // Show prompt if not installed and not dismissed (regardless of browser support)
  const shouldShow = !isInstalled && !dismissed;
  const canUseNativePrompt = deferredPrompt !== null;

  return {
    shouldShow,
    canUseNativePrompt,
    platform,
    isInstalled,
    promptInstall,
    dismiss,
  };
}
